if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.initCustomDots();
        this.initGalleryNavButtons();
        this.updateGalleryControls();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initCustomDots() {
        const viewer = this.elements.viewer;
        const slider = viewer?.querySelector('[id^="Slider-Gallery"]');
        const dots = viewer?.querySelectorAll('.custom-dots .dot');
        if (!dots?.length) return;

        dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            if (dot.dataset.mediaId) this.setActiveMedia(dot.dataset.mediaId, false);
          });
        });

        if (!slider) return;

        slider.addEventListener(
          'scroll',
          debounce(() => {
            if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) return;
            const slides = [...slider.querySelectorAll('.slider__slide[data-media-id]')];
            let activeIndex = 0;
            slides.forEach((slide, index) => {
              if (slider.scrollLeft >= slide.offsetLeft - 10) activeIndex = index;
            });
            dots.forEach((dot, index) => dot.classList.toggle('active', index === activeIndex));
          }, 100)
        );
      }

      initGalleryNavButtons() {
        if (!this.dataset.desktopLayout.includes('thumbnail')) return;

        this.querySelectorAll('[data-gallery-nav]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const slides = [...this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]')];
            if (slides.length < 2) return;

            let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
            index += button.dataset.galleryNav === 'next' ? 1 : -1;
            index = Math.max(0, Math.min(slides.length - 1, index));

            const mediaId = slides[index]?.dataset.mediaId;
            if (mediaId) this.setActiveMedia(mediaId, false);
          });
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        this.elements.viewer?.querySelectorAll('.custom-dots .dot').forEach((dot) => {
          const isActive = dot.dataset.mediaId === mediaId;
          dot.classList.toggle('active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.updateGalleryControls(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      updateGalleryControls(mediaId) {
        const slides = [...(this.elements.viewer?.querySelectorAll('.slider__slide[data-media-id]') || [])];
        if (!slides.length) return;

        let index = mediaId
          ? slides.findIndex((slide) => slide.dataset.mediaId === mediaId)
          : slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (index < 0) index = 0;

        const currentEl = this.querySelector('[data-gallery-counter-current]');
        if (currentEl) currentEl.textContent = String(index + 1);

        const prev = this.querySelector('[data-gallery-nav="previous"]');
        const next = this.querySelector('[data-gallery-nav="next"]');
        if (prev) {
          if (index <= 0) prev.setAttribute('disabled', '');
          else prev.removeAttribute('disabled');
        }
        if (next) {
          if (index >= slides.length - 1) next.setAttribute('disabled', '');
          else next.removeAttribute('disabled');
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
